import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import * as yup from "yup";
import SignUpNavbar from "../../components/SignUpNavbar";
import { useAuth } from "../../context/AuthContext";
import useNationalIdentity, {
  NationalIdentityType,
} from "../../hooks/useNationalIdentity";
import {
  hasNationalIdentitySubmitted,
  isInternationalResident,
  navigateForOnboardingStep,
} from "../../utils/onboardingNextStep";

const PROFILES: Record<
  string,
  {
    type: NationalIdentityType;
    label: string;
    shortLabel: string;
    example: string;
    format: RegExp;
  }
> = {
  US: {
    type: "ssn",
    label: "Social Security Number",
    shortLabel: "SSN",
    example: "123-45-6789",
    format: /^\d{3}-?\d{2}-?\d{4}$/,
  },
  CA: {
    type: "sin",
    label: "Social Insurance Number",
    shortLabel: "SIN",
    example: "123 456 789",
    format: /^\d{3}[ -]?\d{3}[ -]?\d{3}$/,
  },
  GB: {
    type: "nino",
    label: "National Insurance number",
    shortLabel: "NI number",
    example: "QQ 12 34 56 C",
    format: /^[A-CEGHJ-PR-TW-Z]{2}[ -]?\d{2}[ -]?\d{2}[ -]?\d{2}[ -]?[A-D]$/i,
  },
};

const NationalIdentity = () => {
  const navigate = useNavigate();
  const { user, refetch } = useAuth();
  const country = user?.residence_country?.trim().toUpperCase() ?? "";
  const profile = PROFILES[country];

  useEffect(() => {
    if (!user) return;
    if (!isInternationalResident(user) || hasNationalIdentitySubmitted(user)) {
      navigateForOnboardingStep(navigate, null, user);
    }
  }, [navigate, user]);

  const schema = useMemo(
    () =>
      yup.object({
        number: yup
          .string()
          .required(`${profile?.shortLabel ?? "Identity number"} is required`)
          .test(
            "identity-format",
            `Enter a valid ${profile?.shortLabel ?? "identity number"}`,
            (value) => Boolean(value && profile?.format.test(value.trim())),
          ),
        consent: yup
          .boolean()
          .oneOf([true], "You must consent before we can process this identity number"),
      }),
    [profile],
  );

  const { submitNationalIdentity, submittingNationalIdentity, nationalIdentityError } =
    useNationalIdentity({
      async onSuccess(response) {
        await refetch();
        const verified = response.data?.verification?.status === "verified";
        toast.success(
          verified
            ? `${profile.shortLabel} verified successfully`
            : `${profile.shortLabel} saved. Verification can be completed later.`,
        );
        navigate("/sign-up/complete-profile", {
          replace: true,
          state: { nin_data: response.data?.entity ?? {} },
        });
      },
    });

  if (!profile) {
    return (
      <div>
        <SignUpNavbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-5 px-5 text-center">
          <h1 className="text-3xl font-bold text-deepBlue">Country not available yet</h1>
          <p className="max-w-lg text-gray-600">
            International onboarding currently supports the United States, Canada, and the United Kingdom.
          </p>
          <button
            type="button"
            onClick={() => navigate("/sign-up/confirm-residence", { replace: true })}
            className="rounded-lg bg-deepBlue px-6 py-3 font-medium text-white"
          >
            Change country
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {submittingNationalIdentity && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <SignUpNavbar />
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-8 px-5 py-10 text-lg md:px-20">
        <div className="flex max-w-2xl flex-col items-center gap-3 text-center">
          <h1 className="text-3xl font-bold text-deepBlue lg:text-5xl">
            Provide your {profile.shortLabel}
          </h1>
          <p className="text-gray-600">
            We use your {profile.label.toLowerCase()} to confirm your identity. If verification is temporarily unavailable, we will save your submission and let you continue.
          </p>
        </div>

        <Formik
          initialValues={{ number: "", consent: false }}
          validationSchema={schema}
          onSubmit={(values) =>
            submitNationalIdentity({
              type: profile.type,
              number: values.number,
              consent: values.consent,
            })
          }
        >
          {({ isValid, dirty }) => (
            <Form className="flex w-full max-w-md flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="number" className="text-sm font-bold text-gray-700">
                  {profile.label}*
                </label>
                <Field
                  id="number"
                  name="number"
                  type="password"
                  inputMode={profile.type === "nino" ? "text" : "numeric"}
                  autoComplete="off"
                  placeholder={profile.example}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 shadow focus:outline-deepBlue"
                />
                <ErrorMessage name="number" component="div" className="text-sm text-red-500" />
              </div>

              <label className="flex items-start gap-3 text-sm text-gray-600">
                <Field type="checkbox" name="consent" className="mt-1 size-4" />
                <span>
                  I consent to Visaro securely processing this identity number for identity verification. I have read the{" "}
                  <Link to="/terms" target="_blank" rel="noreferrer" className="text-visaro-orange underline">Terms</Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" rel="noreferrer" className="text-visaro-orange underline">Privacy Policy</Link>.
                </span>
              </label>
              <ErrorMessage name="consent" component="div" className="text-sm text-red-500" />

              {nationalIdentityError && (
                <div className="text-sm text-red-500">
                  {nationalIdentityError.message || "Could not save your identity number"}
                </div>
              )}

              <button
                type="submit"
                disabled={!isValid || !dirty || submittingNationalIdentity}
                className="rounded-lg bg-deepBlue px-5 py-4 text-sm font-bold text-white disabled:opacity-60"
              >
                {submittingNationalIdentity ? "Saving..." : "Continue"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NationalIdentity;
